/* eslint-disable no-restricted-syntax */

import { ROUND_SPECIAL_FEATURE_FLAG_NAME } from '../../../constants';

/**
 * Helper function to recursively force a node and its descendants to status 0.
 * This is an absolute override for the status.
 * @param {object} node - The starting node for forcing status to 0.
 */
function forceSubtreeToZero(node) {
  if (!node) return;
  node.status = 0; // Set current node to 0
  // Recursively call for children
  if (node.children) {
    for (const child of node.children) {
      forceSubtreeToZero(child);
    }
  }
}

/**
 * Applies the direct status change to the target node and triggers a cascading
 * effect if the new status is 0. This function mutates the tree directly
 * and returns the target node if found, otherwise null.
 *
 * @param {object} node - The current node being processed in the tree.
 * @param {string} targetName - The 'name' property of the node to be updated.
 * @param {number} newStatus - The new status (0 or 1) to apply.
 * @returns {object|null} The target node if found, otherwise null.
 */
function applyDirectChangeAndDownwardZeroCascade(node, targetName, newStatus) {
  if (!node) return null;

  // Check if the current node is the target node
  if (node.name === targetName) {
    node.status = newStatus; // Apply the direct status change
    // If the target node is set to 0, force its entire subtree (children and grandchildren) to 0.
    if (newStatus === 0) {
      forceSubtreeToZero(node);
    }
    return node; // Return the found target node
  }

  // Continue searching for the target node in children
  if (node.children) {
    for (const child of node.children) {
      const foundNode = applyDirectChangeAndDownwardZeroCascade(child, targetName, newStatus);
      if (foundNode) {
        return foundNode;
      }
    }
  }
  return null; // Target node not found in this branch
}

/**
 * Applies upward propagation of 1s through a post-order traversal.
 * It promotes a parent's status to 1 if any of its children has a status of 1.
 * This promotion only happens if the parent's current status is not already 0.
 *
 * @param {object} node - The current node being processed.
 */
function applyUpwardPromotion(node) {
  if (!node) return;

  // Recursively process children first (post-order traversal).
  // This ensures children's statuses are finalized before their parent evaluates them.
  if (node.children) {
    for (const child of node.children) {
      applyUpwardPromotion(child); // Recursive call to process child
    }
  }

  // After all children are processed, check if any of them have a status of 1.
  let anyChildIsOne = false;
  if (node.children) {
    for (const child of node.children) {
      if (child.status === 1) { // Check the child's *final* status after its own processing
        anyChildIsOne = true;
        break; // No need to check further children if one is already 1
      }
    }
  }

  // Apply Rule 2 (promotion):
  // If this node's current status is 0 AND at least one of its children has a status of 1,
  // then promote this node's status to 1.
  // This rule respects the strong override from the downward cascade: if a node was already set to 0
  // (due to direct change or an ancestor cascade), it will remain 0 and not be promoted back to 1.
  if (node.status === 0 && anyChildIsOne) {
    node.status = 1;
  }
  // Important: This function *only promotes to 1*. It does NOT implicitly demote a node to 0
  // if its children all become 0. A node's status remains 1 unless it is explicitly
  // set to 0 by the downward cascade rule.
}

/**
 * Main function to update the status of a specific node in a nested tree structure
 * and propagate changes according to the defined rules.
 *
 * Rules:
 * 1. If a parent's status is changed to 0, change all the status of children and grandchildren to 0.
 * (This is a strong, absolute override).
 * 2. If one of the child status changes to 1, change all the status of the parent and grandparent to 1.
 * (This is a promotion rule; it does not implicitly demote parents if children become 0).
 *
 * Tree data
 * {
  name: 'round-special', 
  status: 1,
  children: [
  { name: 'round-special-live-feed',
    status: 1,
    children: [ { name: 'round-special-live-feed-enable-odds', status: 1 }]
  },
  { name: 'round-special-prematch-feed',
    status: 1,
    children: [ { name: 'round-special-prematch-feed-enable-odds', status: 1, }]
  },
  { name: 'round-special-widget-ui',
    status: 1,
    children: []
  }
 ]
}
 * @param {object} tree - The root of the tree structure.
 * @param {string} targetName - The 'name' property of the node to be updated.
 * @param {number} newStatus - The new status (0 or 1) to apply to the target node.
 * @returns {object} The modified tree structure.
 */
export function updateTree(tree, targetName, newStatus) {
  // Input validation
  if (!tree || typeof newStatus !== 'number' || (newStatus !== 0 && newStatus !== 1)) {
    // eslint-disable-next-line no-console
    console.error('Invalid input: tree must be an object, newStatus must be 0 or 1.');
    return tree;
  }

  // Pass 1: Apply the direct status change and downward 0-cascades.
  // This function returns the specific node that was targeted for update.
  const targetNode = applyDirectChangeAndDownwardZeroCascade(tree, targetName, newStatus);

  // If the target node was not found during the first pass, log a warning and return the original tree.
  if (!targetNode) {
    // eslint-disable-next-line no-console
    console.warn(`Node with name '${targetName}' not found. No changes applied.`);
    return tree;
  }

  // Pass 2: Apply upward propagation of 1s.
  applyUpwardPromotion(tree);

  return tree; // Return the modified tree
}

export function buildRoundSpecialFeatureTreeStructure(featureFlags) {
  // Create a map for quick lookup of relevant nodes by their name.
  // Also, initialize an empty 'children' array for each node.
  const nodeMap = new Map();
  featureFlags.forEach(item => {
    nodeMap.set(item.name, { ...item, children: [] });
  });

  // Retrieve the specific nodes from the map based on their names.
  // This makes it easier to assign children.
  const roundSpecial = nodeMap.get(ROUND_SPECIAL_FEATURE_FLAG_NAME.ROUND_SPECIAL);
  const liveFeed = nodeMap.get(ROUND_SPECIAL_FEATURE_FLAG_NAME.ROUND_SPECIAL_LIVE_FEED);
  const liveFeedOdds = nodeMap.get(ROUND_SPECIAL_FEATURE_FLAG_NAME.ROUND_SPECIAL_LIVE_FEED_ENABLE_ODDS);
  const prematchFeed = nodeMap.get(ROUND_SPECIAL_FEATURE_FLAG_NAME.ROUND_SPECIAL_PREMATCH_FEED);
  const prematchFeedOdds = nodeMap.get(ROUND_SPECIAL_FEATURE_FLAG_NAME.ROUND_SPECIAL_PREMATCH_FEED_ENABLE_ODDS);
  const widgetUi = nodeMap.get(ROUND_SPECIAL_FEATURE_FLAG_NAME.ROUND_SPECIAL_WIDGET_UI);

  if (!roundSpecial) {
    // eslint-disable-next-line no-console
    console.warn('Root node \'round-special\' not found in the provided data.');
    return null;
  }

  // Build the hierarchy
  if (liveFeed && liveFeedOdds) {
    liveFeed.children.push(liveFeedOdds);
  }

  if (prematchFeed && prematchFeedOdds) {
    prematchFeed.children.push(prematchFeedOdds);
  }

  if (liveFeed) {
    roundSpecial.children.push(liveFeed);
  }

  if (prematchFeed) {
    roundSpecial.children.push(prematchFeed);
  }

  if (widgetUi) {
    roundSpecial.children.push(widgetUi);
  }

  return roundSpecial;
}

export function flattenTree(node, accumulator = []) {
  if (!node) {
    return accumulator;
  }

  accumulator.push({...node});

  // If the current node has children, recursively call flattenTree for each child
  if (node.children && Array.isArray(node.children)) {
    for (const child of node.children) {
      flattenTree(child, accumulator); // Pass the same accumulator
    }
  }

  return accumulator;
}